
/**
 * AutomationCompiler
 *
 * Validates, normalizes, and prepares AutomationModel instances for generators.
 *
 * Architecture rule: generators NEVER receive raw AST models.
 * They always receive the output of AutomationCompiler.compile().
 *
 * Phase A: validation + pass-through normalization.
 * Phase D will add optimization, trigger merging, and diagnostics UI.
 */
import { materializePrimaryEntity } from '../primaryEntity.js';

export class AutomationCompiler {
  /**
   * Compile an array of AutomationModel instances.
   *
   * @param {AutomationModel[]} models
   * @param {{primaryEntity?: string}} [context]
   * @returns {{ models: AutomationModel[], diagnostics: Diagnostic[] }}
   *
   * @typedef {{ severity: 'error'|'warn'|'info', modelId: string, message: string }} Diagnostic
   */
  compile(models, context = {}) {
    const diagnostics = [];
    const compiled    = [];
    const effectiveModels = materializePrimaryEntity(models, context.primaryEntity);

    for (const model of effectiveModels) {
      const issues = this._validate(model);
      diagnostics.push(...issues);

      // Hard errors skip the model; warnings let it through.
      if (issues.some(d => d.severity === 'error')) {
        diagnostics.push({
          severity: 'error',
          modelId:  model.id,
          message:  'Model skipped due to validation errors.',
        });
        continue;
      }

      compiled.push(this._normalize(model));
    }

    return { models: compiled, diagnostics };
  }

  // ── Validation ──────────────────────────────────────────────────────────────

  /** @private */
  _validate(model) {
    const issues = [];

    // State-binding models: property ← strategy, no triggers/actions needed
    if (model.bindings?.length > 0) {
      for (const binding of model.bindings) {
        if (!binding.property) {
          issues.push({ severity: 'warn', modelId: model.id, message: 'State binding has no widget property.' });
        }
        if (binding.sourceType === 'template') {
          issues.push({ severity: 'warn', modelId: model.id, message: 'Template source bindings are deprecated; select a concrete entity.' });
        }
        if (['state', 'mapping', 'switch'].includes(binding.strategy)
            && !String(binding.entityId ?? '').trim()) {
          issues.push({ severity: 'warn', modelId: model.id, message: 'Binding has no concrete entity.' });
        }
      }
      return issues;
    }

    if (!model.triggers.length) {
      issues.push({ severity: 'warn', modelId: model.id, message: 'Automation has no triggers.' });
    }

    if (!model.actions.length) {
      issues.push({ severity: 'warn', modelId: model.id, message: 'Automation has no actions.' });
    }

    for (const action of model.actions) {
      if (action.target?.source === 'template') {
        issues.push({ severity: 'warn', modelId: model.id, message: 'Template source targets are deprecated; select a concrete entity.' });
      }
      // Service calls need either a full service identifier or the legacy
      // provider/service pair; other action types carry data in parameters.
      if (action.type !== 'call_service') continue;
      if (!action.service) {
        issues.push({ severity: 'warn', modelId: model.id, message: 'Service call is missing a service name.' });
      } else if (!action.provider && !String(action.service).includes('.')) {
        issues.push({ severity: 'warn', modelId: model.id, message: 'Action is missing a provider.' });
      }
    }

    for (const condition of model.conditions) {
      if (!condition.left || !condition.right) {
        issues.push({ severity: 'warn', modelId: model.id, message: 'Condition is missing left or right operand.' });
      }
      if (!condition.operator) {
        issues.push({ severity: 'warn', modelId: model.id, message: 'Condition is missing an operator.' });
      }
    }

    return issues;
  }

  // ── Normalization ───────────────────────────────────────────────────────────

  /**
   * Normalize a single model.
   *
   * Phase A: identity (pass-through).
   * Future phases will add:
   *   - Template variable resolution
   *   - Trigger deduplication / merging
   *   - Redundant condition removal
   *
   * @private
   */
  _normalize(model) {
    return model;
  }
}
